import { Bookmark, Download, FileText, Heart, MessageCircle, MoreHorizontal, Pin } from "lucide-react";
import { useState } from "react";
import { MockAvatar } from "@/src/common/components/app-mock/avatar";
import { MockPhoto, MockVideoThumb } from "@/src/common/components/app-mock/media";
import { getEvent, getPerson, type MockFeedPost, type MockPoll, ROLE_CHIP_LABEL } from "@/src/common/components/app-mock/mock-community";
import { MOCK_TOKENS, MockCard, MockChip } from "@/src/common/components/app-mock/primitives";
import { EventCardCompact } from "@/src/common/components/app-mock/web-screens/event-card-compact";
import { useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";

function PollDisplay({ poll }: { poll: MockPoll }) {
	const { palette } = useCommunityVariant();
	return (
		<div className="mt-2 space-y-1.5 rounded-lg border p-2.5" style={{ borderColor: MOCK_TOKENS.separator }} data-mock="poll">
			{poll.options.map((option, index) => {
				const chosen = index === poll.chosenIndex;
				return (
					<div key={option.label} className="relative overflow-hidden rounded-lg border" style={{ borderColor: MOCK_TOKENS.border }}>
						<span
							aria-hidden="true"
							className="absolute inset-y-0 left-0"
							style={{
								width: `${option.percent}%`,
								background: chosen ? `color-mix(in oklab, ${palette.accent} 25%, transparent)` : MOCK_TOKENS.default,
							}}
						/>
						<span className="relative flex items-center justify-between px-2.5 py-1.5 text-[11px]">
							<span className={chosen ? "font-semibold" : "text-neutral-700"} style={chosen ? { color: palette.accent } : undefined}>
								{option.label}
							</span>
							<span className="text-neutral-600">{option.percent}%</span>
						</span>
					</div>
				);
			})}
			<p className="text-[10px] text-neutral-500">{poll.votes} votes</p>
		</div>
	);
}

interface WebPostCardProps {
	post: MockFeedPost;
	interactive?: boolean;
	mediaHeight?: string;
}

export function WebPostCard({ post, interactive, mediaHeight = "h-36" }: WebPostCardProps) {
	const { palette } = useCommunityVariant();
	const author = getPerson(post.authorId);
	const roleLabel = ROLE_CHIP_LABEL[author.role];
	const event = post.eventId ? getEvent(post.eventId) : undefined;
	const [liked, setLiked] = useState(Boolean(post.liked));
	const [bookmarked, setBookmarked] = useState(Boolean(post.bookmarked));
	const likeCount = post.likes + (liked ? 1 : 0) - (post.liked ? 1 : 0);
	const likeIcon = <Heart className="size-3.5" fill={liked ? "currentColor" : "none"} />;
	const bookmarkIcon = <Bookmark className="size-3.5" fill={bookmarked ? "currentColor" : "none"} />;

	return (
		<MockCard dataMock="post-card">
			<div className="flex items-start justify-between gap-2">
				<div className="flex min-w-0 items-center gap-2">
					<MockAvatar person={author} size={28} />
					<div className="min-w-0">
						<div className="flex flex-wrap items-center gap-1.5">
							<span className="truncate text-[11px] font-semibold text-neutral-900">{author.name}</span>
							{roleLabel ? (
								<MockChip color="accent" size="xs">
									{roleLabel}
								</MockChip>
							) : null}
							<span className="text-[10px] text-neutral-500">{post.timeAgo}</span>
						</div>
						{post.groups.length > 0 ? <p className="text-[10px] text-neutral-500">Posted in {post.groups.join(", ")}</p> : null}
					</div>
				</div>
				<div className="flex shrink-0 items-center gap-1 text-neutral-500">
					{post.pinned ? <Pin className="size-3 rotate-45" fill="currentColor" style={{ color: palette.accent }} /> : null}
					{interactive ? (
						<button
							type="button"
							aria-label={bookmarked ? "Remove bookmark" : "Bookmark post"}
							aria-pressed={bookmarked}
							onClick={() => setBookmarked((value) => !value)}
							className="rounded-md p-1 hover:bg-black/5"
							style={bookmarked ? { color: palette.accent } : undefined}
						>
							{bookmarkIcon}
						</button>
					) : (
						<span className="p-1" style={bookmarked ? { color: palette.accent } : undefined} aria-hidden="true">
							{bookmarkIcon}
						</span>
					)}
					<span className="p-1" aria-hidden="true">
						<MoreHorizontal className="size-3.5" />
					</span>
				</div>
			</div>

			<div className="mt-2 space-y-1.5 text-[11px] leading-relaxed text-neutral-800">
				{post.paragraphs.map((paragraph) => (
					<p key={paragraph}>{paragraph}</p>
				))}
			</div>

			{event ? <EventCardCompact event={event} /> : null}
			{post.images ? (
				post.images.length === 1 ? (
					<MockPhoto scene={post.images[0] ?? "workshop"} className={`mt-2 w-full ${mediaHeight}`} />
				) : (
					<div className="mt-2 grid grid-cols-2 gap-1.5">
						{post.images.map((scene) => (
							<MockPhoto key={scene} scene={scene} className="h-28 w-full" />
						))}
					</div>
				)
			) : null}
			{post.video ? (
				<MockVideoThumb scene={post.video.scene} duration={post.video.duration} className={`mt-2 w-full ${mediaHeight}`} />
			) : null}
			{post.poll ? <PollDisplay poll={post.poll} /> : null}
			{post.file ? (
				<div className="mt-2 flex items-center gap-2.5 rounded-lg border px-3 py-2" style={{ borderColor: MOCK_TOKENS.border }}>
					<FileText className="size-5 shrink-0 text-neutral-500" />
					<div className="min-w-0 flex-1">
						<p className="truncate text-[11px] font-medium text-neutral-900">{post.file.name}</p>
						<p className="text-[10px] text-neutral-500">{post.file.meta}</p>
					</div>
					<Download className="size-3.5 shrink-0 text-neutral-500" />
				</div>
			) : null}

			<div
				className="mt-2 flex items-center gap-4 border-t pt-2 text-[11px] text-neutral-500"
				style={{ borderColor: MOCK_TOKENS.separator }}
			>
				{interactive ? (
					<button
						type="button"
						aria-label={liked ? "Unlike post" : "Like post"}
						aria-pressed={liked}
						onClick={() => setLiked((value) => !value)}
						className="inline-flex items-center gap-1 hover:opacity-80"
						style={liked ? { color: palette.accent } : undefined}
					>
						{likeIcon}
						<span>{likeCount > 0 ? likeCount : ""}</span>
					</button>
				) : (
					<span className="inline-flex items-center gap-1" style={liked ? { color: palette.accent } : undefined} aria-hidden="true">
						{likeIcon}
						<span>{likeCount > 0 ? likeCount : ""}</span>
					</span>
				)}
				<span className="inline-flex items-center gap-1" aria-hidden="true">
					<MessageCircle className="size-3.5" />
					<span>{post.comments > 0 ? post.comments : ""}</span>
				</span>
			</div>
		</MockCard>
	);
}
